import React, { Component } from 'react'
import { withRouter } from 'react-router'
import { inject, observer } from 'mobx-react'
import { extendObservable, toJS } from 'mobx'
import PropTypes from 'prop-types'
import { Formik, Form, Field } from 'formik'
import yup from 'yup'
import ProgressBarIndicator from '@components/ProgressBarIndicator'
import { expertApi, userApi } from '@atheneum/api'

import { getSubIndustryOptions } from '@atheneum/api/dist/lib'
import _ from 'lodash'

import ExpertPositionFields from './ExpertPositionFields'

import Button from '@components/Button'
import OptionGroup from '@components/Form/OptionGroup'
import ButtonOption from '@components/OptionGroup/ButtonOption'
import { Autocomplete, Dropdown, TagsInput } from '@components/Form/Autocomplete'
import WarningIcon from '@components/Icon/Warning'
import Select from '@components/Select'
import InfoBox from '@components/InfoBox'
import Spinner from '@components/spinner'
import SherlockReason from '@components/SherlockReason'
import Checkbox from '@components/Checkbox'

import ExpertToProjectFormStore from './expertToProjectForm.store'

class ExpertToProjectForm extends Component {
  static propTypes = {
    staticData: PropTypes.object,
    modalStore: PropTypes.object,
    growlStore: PropTypes.object,
    expertToProjectFormStore: PropTypes.object,
    activeProjectStore: PropTypes.object,
    expertStore: PropTypes.object,
    experts: PropTypes.object,
    history: PropTypes.object,
    isCopy: PropTypes.bool,
    isOneExpert: PropTypes.bool
  }

  constructor(props) {
    super(props)

    const {
      staticData,
      modalStore,
      expertToProjectFormStore,
      experts,
      growlStore,
      activeProjectStore,
      expertSearchStore
    } = props

    extendObservable(this, {
      // The store will exist when going back from the SegmentForm
      expertToProjectFormStore:
        expertToProjectFormStore ||
        new ExpertToProjectFormStore(
          staticData,
          modalStore,
          experts,
          growlStore,
          activeProjectStore,
          props.isCopy,
          expertSearchStore
        ),
      error: null,
      isInvite: false
    })

    this.state = {
      step: 1,
      comments: {},
      expertCreationLoading: false,
      selection: this.props.experts?.length > 1 ? [] : [this.props.experts[0]?.id]
    }
  }

  validateEmail(value) {
    return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
      value
    )
      ? true
      : false
  }

  getValidationSchema() {
    return yup.object().shape({
      projectId: yup
        .number()
        .integer()
        .nullable()
        .required('Project is required'),
      segment: yup
        .object()
        .shape({
          id: yup
            .number()
            .integer()
            .nullable()
            .required(`Segment is required`)
        })
        .nullable(),
      expertList: yup
        .array()
        .of(
          yup.object().shape({
            position: yup
              .object()
              .required('Position is required')
              .typeError('Position is required')
          })
        )
        .min(1, 'At least one expert with relevant experience is required'),
      replyStatusId: yup
        .number()
        .integer()
        .nullable()
    })
  }

  submitForm = async (values, actions) => {
    const { expertToProjectFormStore } = this
    const {
      segments,
      expertListErrors,
      expertListInviteErrors,
      breach
    } = expertToProjectFormStore

    // Set this so we don't have to pass props all around
    expertToProjectFormStore.eplsData = values

    const experts = []
    this.setState({ expertCreationLoading: true })

    for (let expert of expertToProjectFormStore.expertList) {
      if (!Number(expert.id)) {
        await this.props.expertStore.get(expert.id)

        const formExpert = values.expertList.find(e => e.id === expert.id)

        if (formExpert) {
          const expertProps = toJS(this.props.expertStore.expert)

          if (formExpert?.form) {
            Object.keys(formExpert?.form).forEach(key => {
              if (key === 'email') {
                expertProps[key] = formExpert?.form[key]
              } else if (key === 'country') {
                const country = this.props.staticData.countries.filter(
                  c => c.name === formExpert?.form[key]
                )[0]
                expertProps.address.country = country
                expertProps.address.countryId = country.id
                expertProps.address.timezoneId = country.timezoneId
              } else if (key === 'languages') {
                const language = this.props.staticData.languages.filter(
                  c => c.id === parseInt(formExpert?.form[key], 10)
                )[0]
                if (language) {
                  expertProps.languages = [
                    {
                      ...language,
                      languageToUser: {
                        languageProficiencyId: 3
                      }
                    }
                  ]
                }
              } else if (key === 'subIndustries') {
                expertProps.subIndustries = formExpert?.form[key]
              } else {
                expertProps.expert[key] = formExpert?.form[key]
                expertProps[key] = formExpert?.form[key]
              }
            })
          }

          const checked = await expertApi.checkExpert({
            emails: expertProps.emails
              ? expertProps.emails.map(e => e.email)
              : undefined,
            fullName: expertProps.fullName,
            linkedinVanityName: expertProps.linkedinVanityName,
            link: expertProps.linkedinVanityName
              ? `https://www.linkedin.com/in/${expertProps.linkedinVanityName}`
              : undefined
          })

          let createdExpert

          try {
            if (checked.id) {
              createdExpert = await expertApi.getOneById(checked.id)
            } else {
              createdExpert = await expertApi.create(expertProps)
            }
          } catch (error) {
            this.setState({ expertCreationLoading: false })
            actions.setSubmitting(false)
            this.props.growlStore.showError(error)
            return
          }

          const cName = expert.position.company.name
            ? expert.position.company.name
            : expert.position.company

          experts.push({
            ...expert,
            ...createdExpert,
            pdlId: expert.id,
            position: {
              ...expert.position,
              company: createdExpert.cv?.experiences?.filter(
                e => e.company.name === cName
              )[0]?.company,
              cvId: createdExpert.cv?.id,
              id: createdExpert.cv?.experiences?.filter(
                e =>
                  e.company.name.toLowerCase().trim() ===
                  cName.toLowerCase().trim()
              )[0]?.id
            }
          })
        }
      } else {
        const formExpert = values.expertList.find(e => e.id === expert.id)
        if (formExpert) {
          const updatedExpert = Object.assign(
            {},
            { ...expert },
            { position: formExpert.position }
          )
          experts.push(updatedExpert)
        }
      }
    }

    this.setState({ expertCreationLoading: false })

    const valueList = []
    for (const e of experts) {
      const expert = experts.find(ex => ex.id === e.id)

      if (e && e.sherlock && !e.sherlock.allowed) {
        e.sherlock = expert?.sherlock
        e.id = expert?.id

        valueList.push(e)
      }
    }

    await expertToProjectFormStore.validateExpertEmails(experts)

    if (this.isInvite) {

      if (expertListErrors.length || expertListInviteErrors.length) {
        actions.setSubmitting(false)
        return
      }

      const { modalStore } = this.props
      const segment = segments.find(s => s.id === values.segment.id)

      modalStore.close()
      modalStore.show({
        type: 'SEGMENT_FORM',
        props: {
          formValues: {
            ...values,
            expertList: experts,
            comments: this.state.comments
          },
          project: values.project,
          segment,
          expertToProjectFormStore,
          resetPinned: this.props.resetPinned
        }
      })

    } else {
      expertToProjectFormStore.expertListInviteErrors.replace([])

      if (expertListErrors.length) {
        actions.setSubmitting(false)
        return
      }

      try {
        await Promise.all([
          breach(
            valueList,
            this.state.comments,
            this.props.activeProjectStore?.activeProjectName,
            this.props.userStore.user.id
          ),
          expertToProjectFormStore.createEPLs(experts, this.props.history)
        ])

        const { expertStore } = this.props
        const currentExpertIsAdded = !!values.expertList.find(
          e => e.id === expertStore.id
        )
        if (currentExpertIsAdded) {
          const {
            REPLY_STATUSES,
            EXPERT_STATUSES
          } = this.props.staticData.CONST
          const allowedStatuses = [
            REPLY_STATUSES.CONFIRMED,
            REPLY_STATUSES.APPLIED,
            REPLY_STATUSES.INTERESTED
          ]

          if (
            expertStore.expert.expert.expertStatusId ===
            EXPERT_STATUSES.DORMANT_EXPERT &&
            allowedStatuses.includes(values.replyStatusId)
          ) {
            expertStore.get(expertStore.id)
          }
        }
      } catch (err) {
        this.error = err.message
        actions.setSubmitting(false)
      }

      await this.props.resetPinned()
    }
  }

  removeExperts = (ids, form) => {
    const store = this.expertToProjectFormStore
    store.expertList = store.expertList.filter(e => !ids.includes(e.id))
    const newList = form.values?.expertList?.filter(e => !ids.includes(e.id))
    form.setFieldValue('expertList', newList, true)
  }

  updateErrors = errorList => {
    this.expertToProjectFormStore.updateAllErrors(errorList)
  }

  updateWarnings = warningList => {
    this.expertToProjectFormStore.updateWarnings(warningList)
  }

  render() {
    const { modalStore } = this.props
    const { expertToProjectFormStore } = this
    const { isCopy } = expertToProjectFormStore

    if (expertToProjectFormStore.loading) return <Spinner />

    const { staticData, isOneExpert } = this.props
    const { REPLY_STATUSES } = staticData.CONST

    const inviteOptions = [
      {
        name: 'As interested',
        value: REPLY_STATUSES.INTERESTED
      },
      {
        name: 'As applied',
        value: REPLY_STATUSES.APPLIED
      },
      {
        name: 'As confirmed',
        value: REPLY_STATUSES.CONFIRMED
      },
      {
        name: 'As not contacted',
        value: REPLY_STATUSES.NOT_CONTACTED
      }
    ]

    return (
      <div>
        <Formik
          initialValues={expertToProjectFormStore.getInitialValues()}
          validationSchema={this.getValidationSchema()}
          onSubmit={this.submitForm}
          render={form => {
            if (!this.ids) {
              this.ids = []
            }

            if (this.ids?.length !== form.values?.expertList?.length) {
              const remove = _.difference(
                this.ids,
                form.values?.expertList?.map(e => e.id)
              )

              this.ids = form.values?.expertList?.map(e => e.id)

              const comments = this.state.comments

              remove.forEach(r => delete comments[r])

              this.setState({ comments })
            }

            const incompleteExperts = form.values.expertList.filter(e => {
              if (!Number(e.id)) {
                return (
                  !e.expert.title ||
                  !e.expert.country ||
                  !e.email ||
                  e.languages?.length === 0 ||
                  e.expert.emails?.length === 0 ||
                  (e.expert.subIndustries?.length && e.expert.subIndustries[0].id === 133) // Other
                )
              }

              return false
            })

            const hasMissingFields = incompleteExperts.length > 0
            const missingFields = {}
            incompleteExperts.forEach(e => {
              const missingKeys = []
              if (!e.email) {
                missingKeys.push('email')
              }
              if (!e.expert.country) {
                missingKeys.push('country')
              }
              if (!e.expert.title) {
                missingKeys.push('title')
              }
              if (e.languages?.length === 0) {
                missingKeys.push('languages')
              }
              // 133 is id for "Other"
              if (e.expert.subIndustries?.length && e.expert.subIndustries[0].id === 133) {
                missingKeys.push('subIndustries')
              }

              missingFields[e.id] = missingKeys
            })
            const notFilledMissingFields =
              incompleteExperts.filter(
                e =>
                  !e.form ||
                  missingFields[e.id].filter(key => {
                    return !e.form[key]
                  }).length > 0 ||
                  missingFields[e.id].filter(key => {
                    return e.form?.errors && e.form?.errors[key]
                  }).length > 0 ||
                  (e.form?.subIndustries?.length && e.form.subIndustries[0].id === '133') // Other
              ).length > 0

            return (
              <div className="expert-form expert-to-project-form">
                {this.state.expertCreationLoading ? (
                  <div className="overlay">
                    <Spinner />
                  </div>
                ) : null}
                <h1 className="expert-form__title">
                  {isCopy ? 'Copy' : 'Add'}/Invite expert to project
                </h1>
                {this.error ? (
                  <InfoBox theme="red" icon={<WarningIcon />}>
                    {this.error}
                  </InfoBox>
                ) : null}
                <Form onSubmit={form.handleSubmit}>
                  {this.state.step === 1 ? (
                    <>
                      <div className="expert-form__section">
                        <h2 className="expert-form__subtitle">
                          Project and segment
                        </h2>
                        <Field
                          name="project"
                          label="Project"
                          data-cy="project-autocomplete"
                          placeholder=""
                          itemToString={item => item && item.projectName}
                          loadOptions={
                            expertToProjectFormStore.handleProjectAutocomplete
                          }
                          onChange={value => {
                            form.setFieldValue('segment', { id: null }, false)
                            expertToProjectFormStore.onProjectChange(
                              value,
                              form
                            )
                          }}
                          component={Autocomplete}
                        />
                        <Field
                          name="segment"
                          label={`Expert's segment`}
                          placeholder={
                            form.values.projectId
                              ? 'Select segment'
                              : 'Please select project'
                          }
                          itemToString={item => item && item.name}
                          options={toJS(expertToProjectFormStore.segments)}
                          onChange={option =>
                            form.setFieldValue(
                              'segment',
                              { id: option.id },
                              false
                            )
                          }
                          data-cy="segment-dropdown"
                          disabled={!form.values.projectId}
                          component={Dropdown}
                        />
                      </div>
                      <div className="expert-form__section">
                        <h2 className="expert-form__subtitle">
                          Expert(s) and relevant positions
                        </h2>
                        {form.values?.expertList?.length > 1 &&
                          form.values?.expertList?.filter(
                            e => !e.sherlock?.allowed
                          ).length > 0 && (
                            <div className="sherlock-warning">
                              {form.values?.expertList?.filter(
                                e => e.sherlock?.allowed === false
                              ).length > 0 &&
                                form.values?.expertList?.filter(
                                  e => e.sherlock?.allowed === false
                                ).length !==
                                Object.values(this.state.comments).map(e => e)
                                  .length
                                ? 'There are experts on your list which sherlock believes you should not use. If you still want to use those experts please write your comment.'
                                : 'Thank you for your comments.'}
                              <div className="sherlock-warning-actions">
                                <Button
                                  onClick={() => {
                                    this.setState({ step: 2 })
                                  }}>
                                  <strong>
                                    {form.values?.expertList?.filter(
                                      e => e.sherlock?.allowed === false
                                    ).length > 0 &&
                                      form.values?.expertList?.filter(
                                        e => e.sherlock?.allowed === false
                                      ).length !==
                                      Object.values(this.state.comments).map(
                                        e => e
                                      ).length
                                      ? 'Write '
                                      : 'Edit '}
                                    Comment
                                  </strong>
                                </Button>
                                <Button
                                  onClick={() => {
                                    const ids = form.values?.expertList?.filter(e => e.sherlock?.allowed === false).map(e => e.id)
                                    this.removeExperts(ids, form)
                                  }}>
                                  <strong>Remove Blocked Ones</strong>
                                </Button>
                              </div>
                            </div>
                          )}
                        {form.values?.expertList?.length === 1 &&
                          form.values?.expertList?.filter(
                            e => !e.sherlock?.allowed
                          ).length > 0 && (
                            <div className="sherlock-warning">
                              {
                                form.values.expertList[0].sherlock
                                  ?.display_reason
                              }
                              <div className="sherlock-warning-actions">
                                <Button
                                  onClick={() => {
                                    this.setState({ step: 2 })
                                  }}>
                                  <strong>Write Comment</strong>
                                </Button>
                              </div>
                            </div>
                          )}
                        <ExpertPositionFields
                          form={form}
                          expertListErrors={
                            expertToProjectFormStore.expertListErrors
                          }
                          expertListWarnings={
                            expertToProjectFormStore.expertListWarnings
                          }
                          expertListInviteErrors={
                            expertToProjectFormStore.expertListInviteErrors
                          }
                          sherlockLoading={
                            this.props.expertSearchStore.sherlockLoading
                          }
                          updateErrors={this.updateErrors}
                          updateWarnings={this.updateWarnings}
                          experts={form.values.expertList}
                          isOneExpert={isOneExpert}
                          comments={this.state.comments}
                        />
                      </div>
                      {Boolean(
                        this.expertToProjectFormStore.expertListErrors.length
                      ) && (
                          <div className="expert-form__section">
                            <span className="red-text">
                              There are{' '}
                              {
                                this.expertToProjectFormStore.expertListErrors
                                  .length
                              }{' '}
                              experts that are not eligable for this project.
                              Scroll up for details.
                              <button
                                className="red-text"
                                title="Click to auto remove all experts that can't be added."
                                onClick={() => {
                                  this.removeExperts(
                                    this.expertToProjectFormStore.expertListErrors.map(
                                      e => e.expertId
                                    ),
                                    form
                                  )
                                  this.expertToProjectFormStore.expertListErrors = []
                                }}>
                                <strong>remove</strong>
                              </button>
                            </span>
                          </div>
                        )}
                      {Boolean(
                        this.expertToProjectFormStore.expertListInviteErrors
                          .length
                      ) && (
                          <div className="expert-form__section">
                            <span className="red-text">
                              There are{' '}
                              {
                                this.expertToProjectFormStore
                                  .expertListInviteErrors.length
                              }{' '}
                              experts that are not eligable for invite due to
                              missing email. Scroll up for details.
                              <button
                                title="Click to auto remove all experts that can't be added."
                                className="red-text"
                                onClick={() => {
                                  this.removeExperts(
                                    this.expertToProjectFormStore.expertListInviteErrors.map(
                                      e => e.expertId
                                    ),
                                    form
                                  )
                                  this.expertToProjectFormStore.expertListInviteErrors = []
                                }}>
                                <strong>remove</strong>
                              </button>
                            </span>
                          </div>
                        )}
                      <div />
                      {hasMissingFields && (
                        <div className="expert-form__section">
                          <h2 className="expert-form__subtitle">
                            Missing Fields
                          </h2>
                          {incompleteExperts.map(expert => {
                            let formExpert
                            let index
                            form.values.expertList.forEach((e, i) => {
                              if (e.id === expert.id) {
                                formExpert = e
                                index = i
                              }
                            })

                            return (
                              <div
                                key={`${expert.id}-mf`}
                                className="missing-wrapper">
                                <div className="missing-label">
                                  {expert.fullName}
                                </div>
                                {!formExpert.email && (
                                  <div
                                    className={`missing-field expert-form__input-wrapper${formExpert.form?.errors?.email
                                      ? ' expert-form__input-wrapper--error'
                                      : ''
                                      }`}>
                                    <label>Email</label>
                                    <input
                                      name="email"
                                      placeholder="Email"
                                      value={formExpert.form?.email || ''}
                                      onChange={async e => {
                                        let payload = { email: e.target.value }

                                        form.setFieldValue(
                                          `expertList[${index}].form.email`,
                                          e.target.value,
                                          false
                                        )

                                        try {
                                          if (
                                            this.validateEmail(e.target.value)
                                          ) {
                                            const check = await userApi.checkEmail(
                                              payload
                                            )

                                            if (!check.valid) {
                                              form.setFieldValue(
                                                `expertList[${index}].form.errors.email`,
                                                'Email is already used.',
                                                false
                                              )
                                            } else {
                                              form.setFieldValue(
                                                `expertList[${index}].form.errors.email`,
                                                undefined,
                                                false
                                              )
                                            }
                                          } else {
                                            form.setFieldValue(
                                              `expertList[${index}].form.errors.email`,
                                              'Email format invalid.',
                                              false
                                            )
                                          }
                                        } catch (error) {
                                          console.error(error.message)
                                        }
                                      }}
                                    />
                                    <div className="expert-form__input-error">
                                      {formExpert.form?.errors?.email}
                                    </div>
                                  </div>
                                )}
                                {!formExpert.expert.country && (
                                  <div className="missing-field">
                                    <label>Country</label>
                                    <Select
                                      value={formExpert.form?.country || ''}
                                      options={[
                                        { label: 'Select Country', value: '' },
                                        ...this.props.staticData.countries.map(
                                          c => ({
                                            label: c.name,
                                            value: c.name
                                          })
                                        )
                                      ]}
                                      onChange={e => {
                                        form.setFieldValue(
                                          `expertList[${index}].form.country`,
                                          e.target.value,
                                          false
                                        )
                                      }}
                                    />
                                  </div>
                                )}
                                {formExpert.languages.length === 0 && (
                                  <div className="missing-field">
                                    <label>Languages</label>
                                    <Select
                                      value={formExpert.form?.languages || ''}
                                      options={[
                                        { label: 'Select Language', value: '' },
                                        ...this.props.staticData.languages.map(
                                          c => ({
                                            label: c.name,
                                            value: c.id
                                          })
                                        )
                                      ]}
                                      onChange={e => {
                                        form.setFieldValue(
                                          `expertList[${index}].form.languages`,
                                          e.target.value,
                                          false
                                        )
                                      }}
                                    />
                                  </div>
                                )}
                                {!formExpert.expert.title && (
                                  <div className="missing-field">
                                    <label>Title</label>
                                    <Select
                                      value={formExpert.form?.title || ''}
                                      options={[
                                        { label: 'Select Title', value: '' },
                                        ...this.props.staticData.titles.map(
                                          c => ({
                                            label: c,
                                            value: c
                                          })
                                        )
                                      ]}
                                      onChange={e => {
                                        form.setFieldValue(
                                          `expertList[${index}].form.title`,
                                          e.target.value,
                                          false
                                        )
                                      }}
                                    />
                                  </div>
                                )}
                                {formExpert.expert.subIndustries[0].id === 133 && (
                                  <div className="missing-field">
                                    <Field
                                      name={`expertList[${index}].form.subIndustries`}
                                      label='Industry / subindustry'
                                      itemToString={item => item && item.name}
                                      options={getSubIndustryOptions(staticData)}
                                      component={TagsInput}
                                      value={formExpert.form?.subIndustries || []}
                                    />
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )}
                      <div>
                        {this.expertToProjectFormStore.progress.submitting && (
                          <ProgressBarIndicator
                            target={
                              this.expertToProjectFormStore.progress.valid
                            }
                            theme="blue"
                            delivered={
                              this.expertToProjectFormStore.progress.processed
                            }
                            label="Creating EPL's"
                            percent={Math.ceil(
                              ((this.expertToProjectFormStore.progress
                                .processed +
                                this.expertToProjectFormStore.progress.failed) /
                                this.expertToProjectFormStore.progress.valid) *
                              100
                            )}
                          />
                        )}
                      </div>
                      <div className="expert-form__section">
                        <div className="segment-actions">
                          <div>
                            <Button
                              button
                              primary
                              onClick={event => {
                                event.preventDefault()
                                this.isInvite = true
                                return form.handleSubmit(event)
                              }}
                              disabled={
                                form.isSubmitting ||
                                (hasMissingFields && notFilledMissingFields) ||
                                (form.values?.expertList?.filter(
                                  e => e.sherlock?.allowed === false
                                ).length > 0 &&
                                  form.values?.expertList?.filter(
                                    e => e.sherlock?.allowed === false
                                  ).length !==
                                  Object.values(this.state.comments).map(
                                    e => e
                                  ).length)
                              }>
                              {isCopy ? 'Copy ' : 'Add '}and invite
                            </Button>
                            {isCopy && (
                              <Button
                                secondary
                                button
                                onClick={event => {
                                  event.preventDefault()
                                  this.isInvite = false
                                  return form.handleSubmit(event)
                                }}
                                disabled={
                                  form.isSubmitting ||
                                  (hasMissingFields &&
                                    notFilledMissingFields) ||
                                  (form.values?.expertList?.filter(
                                    e => e.sherlock?.allowed === false
                                  ).length > 0 &&
                                    form.values?.expertList?.filter(
                                      e => e.sherlock?.allowed === false
                                    ).length !==
                                    Object.values(this.state.comments).map(
                                      e => e
                                    ).length)
                                }>
                                Copy only
                              </Button>
                            )}
                          </div>
                          <div
                            className="cancel-link link m-l-lg"
                            onClick={modalStore.close}>
                            Close
                          </div>
                        </div>
                        {!isCopy && (
                          <>
                            <div className="hr-separator hr-separator--form" />
                            <h2 className="expert-form__subtitle">Add only</h2>
                            <div className="segment-actions">
                              <Field
                                single
                                name="replyStatusId"
                                items={inviteOptions}
                                className="form--multiple-buttons"
                                itemToString={item => item && item.name}
                                onChange={option => {
                                  this.isInvite = false
                                  form.setFieldValue(
                                    'replyStatusId',
                                    option[0].value,
                                    false
                                  )
                                  return form.handleSubmit({
                                    preventDefault: () => { }
                                  })
                                }}
                                renderItem={({ item, className, ...props }) => (
                                  <ButtonOption
                                    {...props}
                                    item={item}
                                    className="button button--primary"
                                    disabled={
                                      form.isSubmitting ||
                                      (hasMissingFields &&
                                        notFilledMissingFields) ||
                                      (form.values?.expertList?.filter(
                                        e => e.sherlock?.allowed === false
                                      ).length > 0 &&
                                        form.values?.expertList?.filter(
                                          e => e.sherlock?.allowed === false
                                        ).length !==
                                        Object.values(
                                          this.state.comments
                                        ).map(e => e).length)
                                    }
                                  />
                                )}
                                component={OptionGroup}
                              />
                              <div
                                className="cancel-link link m-l-lg"
                                onClick={modalStore.close}>
                                Close
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="expert-form__section">
                        <h2 className="expert-form__subtitle">
                          Project and segment
                        </h2>
                        <div className="expert-form__section--actions">
                          <div>Please write a comment for the expert(s)</div>
                          {form.values?.expertList?.length > 1 && (
                            <a
                              href
                              onClick={e => {
                                e.preventDefault()
                                this.setState({
                                  selection: form.values.expertList
                                    .filter(e => e.sherlock?.allowed === false)
                                    .map(e => e.id)
                                })
                              }}>
                              Choose all
                            </a>
                          )}
                        </div>
                        {form.values?.expertList?.length > 1 && (
                          <div className="expert-selection">
                            {form.values.expertList
                              .filter(e => e.sherlock?.allowed === false)
                              .map(e => (
                                <div className="expert-selection-item">
                                  <div className="expert-selection-item-select">
                                    <Checkbox
                                      className="single__checkbox"
                                      onChange={() => {
                                        let selection = this.state.selection
                                        if (
                                          this.state.selection.includes(e.id)
                                        ) {
                                          const index = selection.indexOf(e.id)
                                          if (index !== -1) {
                                            selection.splice(index, 1)
                                          }
                                        } else {
                                          selection.push(e.id)
                                        }

                                        this.setState({ selection })
                                      }}
                                      value={this.state.selection.includes(
                                        e.id
                                      )}
                                    />
                                  </div>
                                  <div
                                    className={`expert-selection-item-label${this.state.comments[e.id]
                                      ? ''
                                      : ' red-text'
                                      }`}
                                    onClick={() =>
                                      this.setState({ selection: [e.id] })
                                    }>
                                    {e.fullName}
                                  </div>
                                  <SherlockReason
                                    sherlock={e.sherlock}
                                    iconOnly
                                  />
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                      <div className="expert-form__section">
                        <textarea
                          rows={5}
                          placeholder={'Please write you comment'}
                          onChange={e => {
                            const comments = this.state.comments
                            if (
                              form.values.expertList.length === 1 &&
                              this.state.selection.length === 0
                            ) {
                              this.setState({
                                selection: [form.values.expertList[0].id]
                              })
                              comments[form.values.expertList[0].id] =
                                e.target.value
                            } else {
                              for (const key of this.state.selection) {
                                comments[key] = e.target.value
                              }
                            }

                            this.setState({ comments })
                          }}
                          value={
                            this.state.comments[this.state.selection[0]]
                              ? this.state.comments[this.state.selection[0]]
                              : ''
                          }
                        />
                      </div>
                      <div className="expert-form__section">
                        <div className="segment-actions">
                          <div>
                            <Button
                              button
                              primary
                              onClick={event => {
                                event.preventDefault()
                                this.setState({ step: 1 })
                              }}>
                              Ok
                            </Button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </Form>
              </div>
            )
          }}
        />
      </div>
    )
  }
}

ExpertToProjectForm.defaultProps = {
  hideButtons: true,
  showCloseIcon: true,
  shouldCloseOnOverlayClick: false
}

export default withRouter(
  inject(
    'staticData',
    'modalStore',
    'growlStore',
    'expertStore',
    'expertSearchStore',
    'activeProjectStore',
    'userStore'
  )(observer(ExpertToProjectForm))
)
